import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assignStudentsToTeacher, getTeachersForCourse } from '../../api/teachers';
import { getCourse, getCourseEnrollments } from '../../api/courses';
import { extractErrorMessage } from '../../utils/api';
import { useI18n } from '../../i18n/I18nContext';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import { formatDate } from '../../utils/datetime';
import './Teachers.css';

// The roster is read 100 at a time (the backend's page limit) and searched by the backend, so a
// course with more students than that is still reachable.
const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;
// How the backend words a student who cannot be assigned; shown translated.
const NOT_ENROLLED_REASON = 'Student is not enrolled in this course';

const getStudentId = (enrollment) => enrollment.userId || enrollment.user?.id || enrollment.id;
const getStudentLabel = (enrollment) => enrollment.user?.name || enrollment.user?.email || enrollment.user?.phone || '';
// Only an enrollment that still gives access can be assigned: the backend refuses the rest.
const isAssignable = (enrollment) => enrollment.isActive !== false;

function AssignStudents() {
  const { t } = useI18n();
  const { courseId, teacherId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsMeta, setStudentsMeta] = useState(null);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState('');
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [teacherNotFound, setTeacherNotFound] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // Students the backend refused in the last assignment: [{ studentId, name, reason }]
  const [notAssigned, setNotAssigned] = useState([]);
  const [search, setSearch] = useState('');
  // What the roster is searched with: `search` a beat after the last keystroke.
  const [query, setQuery] = useState('');
  const [retryTick, setRetryTick] = useState(0);
  const [canRetryAssign, setCanRetryAssign] = useState(false);
  // Names of every student seen, so a refusal for someone found by an earlier search still has a name.
  const studentNames = useRef(new Map());
  // Only the latest roster request may touch the list.
  const requestId = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setLoadError('');
      setTeacherNotFound(false);
      try {
        const [courseData, teachersData] = await Promise.all([
          getCourse(courseId),
          getTeachersForCourse(courseId),
        ]);

        if (!cancelled) {
          setCourse(courseData);

          const foundTeacher = teachersData.find((tc) => tc.id === teacherId);
          setTeacher(foundTeacher || null);
          setTeacherNotFound(!foundTeacher);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(extractErrorMessage(err, t('teacherAdmin.assign.loadFailed')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, teacherId, retryTick]);

  const handleRetryLoad = useCallback(() => setRetryTick((n) => n + 1), []);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Page 1 of the roster for the current search, or the next page appended to it.
  const loadStudents = useCallback(async (pageNo, append) => {
    const id = ++requestId.current;
    setStudentsLoading(true);
    setStudentsError('');
    try {
      const res = await getCourseEnrollments(courseId, {
        page: pageNo,
        limit: PAGE_SIZE,
        search: query || undefined,
      });
      if (id !== requestId.current) return;
      const rows = Array.isArray(res) ? res : (res?.data || []);
      rows.forEach((e) => studentNames.current.set(getStudentId(e), getStudentLabel(e)));
      setStudents((prev) => {
        if (!append) return rows;
        const seen = new Set(prev.map((e) => e.id));
        return [...prev, ...rows.filter((e) => !seen.has(e.id))];
      });
      setStudentsMeta(res?.meta || null);
    } catch (err) {
      if (id !== requestId.current) return;
      if (!append) {
        // Not the rows of the search before this one.
        setStudents([]);
        setStudentsMeta(null);
      }
      setStudentsError(extractErrorMessage(err, t('teacherAdmin.assign.loadFailed')));
    } finally {
      if (id === requestId.current) setStudentsLoading(false);
    }
  }, [courseId, query, t]);

  useEffect(() => {
    loadStudents(1, false);
  }, [loadStudents, retryTick]);

  const hasMoreStudents = Boolean(studentsMeta?.hasNext);
  const loadMoreStudents = () => loadStudents((studentsMeta?.page || 1) + 1, true);

  const handleSelectStudent = (enrollment) => {
    const sid = getStudentId(enrollment);
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(sid)) {
      newSelected.delete(sid);
    } else {
      newSelected.add(sid);
    }
    setSelectedStudents(newSelected);
  };

  const assignableStudents = students.filter(isAssignable);
  const allSelected = assignableStudents.length > 0
    && assignableStudents.every((e) => selectedStudents.has(getStudentId(e)));

  // Selects (or clears) the students on screen that can be assigned, keeping any picked earlier.
  const handleSelectAll = () => {
    const next = new Set(selectedStudents);
    assignableStudents.forEach((e) => (allSelected ? next.delete(getStudentId(e)) : next.add(getStudentId(e))));
    setSelectedStudents(next);
  };

  const handleAssign = async () => {
    if (selectedStudents.size === 0) {
      setError(t('teacherAdmin.assign.selectAtLeastOne'));
      setCanRetryAssign(false);
      return;
    }

    setAssigning(true);
    setError('');
    setCanRetryAssign(false);
    setSuccessMessage('');
    setNotAssigned([]);

    try {
      const response = await assignStudentsToTeacher(courseId, teacherId, {
        studentIds: Array.from(selectedStudents),
      });

      // The backend answers 200 even when some students were refused: they come back in `errors`.
      const refused = Array.isArray(response.errors) ? response.errors : [];
      if (response.assigned > 0 || response.skipped > 0 || refused.length === 0) {
        const successText = t('teacherAdmin.assign.assignSuccess', { assigned: response.assigned });
        const skippedText = response.skipped > 0
          ? ` ${t('teacherAdmin.assign.alreadyAssignedSuffix', { skipped: response.skipped })}`
          : '';
        setSuccessMessage(`${successText}${skippedText}`);
      }
      setNotAssigned(refused.map((item) => ({
        studentId: item.studentId,
        name: studentNames.current.get(item.studentId) || item.studentId,
        reason: item.reason === NOT_ENROLLED_REASON ? t('teacherAdmin.assign.reasonNotEnrolled') : item.reason,
      })));
      setSelectedStudents(new Set());
    } catch (err) {
      setError(extractErrorMessage(err, t('teacherAdmin.assign.assignFailed')));
      setCanRetryAssign(true);
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="teachers-page">
        <h1>{t('teacherAdmin.assign.pageTitle')}</h1>
        <LoadingState skeleton="table" rows={5} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="teachers-page">
        <h1>{t('teacherAdmin.assign.pageTitle')}</h1>
        <PageEmptyState
          variant="error"
          title={t('teacherAdmin.assign.loadFailed')}
          description={loadError !== t('teacherAdmin.assign.loadFailed') ? loadError : undefined}
          actionLabel={t('teacherAdmin.assign.retry')}
          onAction={handleRetryLoad}
        />
      </div>
    );
  }

  if (teacherNotFound) {
    return (
      <div className="teachers-page">
        <h1>{t('teacherAdmin.assign.pageTitle')}</h1>
        <PageEmptyState
          variant="error"
          title={t('teacherAdmin.assign.teacherNotFound')}
          actionLabel={t('teacherAdmin.assign.backToTeachers')}
          onAction={() => navigate(`/courses/${courseId}/teachers`)}
        />
      </div>
    );
  }

  return (
    <div className="teachers-page">
      <div className="teachers-header">
        <h1>
          {t('teacherAdmin.assign.heading', {
            teacher: teacher ? teacher.name : t('teacherAdmin.assign.teacherFallback'),
            course: course?.title,
          })}
        </h1>
        <button
          type="button"
          onClick={() => navigate(`/courses/${courseId}/teachers`)}
          className="teachers-back-btn"
        >
          {t('teacherAdmin.assign.backToTeachers')}
        </button>
      </div>

      <div className="teachers-content">
        {error && (
          <div className="teachers-error">
            {error}
            {canRetryAssign && (
              <button
                type="button"
                onClick={handleAssign}
                className="ds-btn ds-btn-secondary ds-btn-sm teachers-error-retry"
              >
                {t('teacherAdmin.assign.retry')}
              </button>
            )}
          </div>
        )}
        {successMessage && <div className="teachers-success">{successMessage}</div>}
        {/* With no rows the empty state below carries the error and the retry; this is a failed "load more". */}
        {studentsError && students.length > 0 && (
          <div className="teachers-error" role="alert">{studentsError}</div>
        )}
        {notAssigned.length > 0 && (
          <div className="teachers-warning" role="alert">
            <p>{t('teacherAdmin.assign.notAssignedTitle', { count: notAssigned.length })}</p>
            <ul>
              {notAssigned.map((item) => (
                <li key={item.studentId}>
                  <strong>{item.name}</strong> — {item.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="assign-students-card">
          <div className="assign-controls">
            <input
              type="text"
              className="teachers-search-input"
              placeholder={t('teacherAdmin.assign.searchPlaceholder')}
              aria-label={t('teacherAdmin.assign.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <button
              type="button"
              onClick={handleAssign}
              className="teachers-submit-btn"
              disabled={assigning || selectedStudents.size === 0}
            >
              {assigning
                ? t('teacherAdmin.assign.assigning')
                : t('teacherAdmin.assign.assignButton', { count: selectedStudents.size })}
            </button>
          </div>

          <div className="assign-students-table-wrap">
            <table className="teachers-table">
              <thead>
                <tr>
                  <th width="40">
                    <input
                      type="checkbox"
                      aria-label={t('teacherAdmin.assign.selectAllStudents')}
                      checked={allSelected}
                      onChange={handleSelectAll}
                      disabled={assigning || assignableStudents.length === 0}
                    />
                  </th>
                  <th>{t('teacherAdmin.assign.nameHeader')}</th>
                  <th>{t('teacherAdmin.assign.emailHeader')}</th>
                  <th>{t('teacherAdmin.assign.progressHeader')}</th>
                  <th>{t('teacherAdmin.assign.enrolledDateHeader')}</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: 0, background: 'transparent', border: 'none' }}>
                      {studentsLoading ? (
                        <LoadingState skeleton="table" rows={3} />
                      ) : studentsError ? (
                        <PageEmptyState
                          variant="error"
                          size="sm"
                          title={t('teacherAdmin.assign.loadFailed')}
                          description={studentsError !== t('teacherAdmin.assign.loadFailed') ? studentsError : undefined}
                          actionLabel={t('teacherAdmin.assign.retry')}
                          onAction={handleRetryLoad}
                        />
                      ) : query ? (
                        <PageEmptyState
                          variant="search"
                          size="sm"
                          title={t('teacherAdmin.assign.noMatch')}
                          actionLabel={t('teacherAdmin.assign.noMatchAction')}
                          onAction={() => setSearch('')}
                        />
                      ) : (
                        <PageEmptyState
                          variant="students"
                          size="sm"
                          title={t('teacherAdmin.assign.noneEnrolled')}
                          actionLabel={t('teacherAdmin.assign.noneEnrolledAction')}
                          onAction={() => navigate(`/courses/${courseId}/enrollments`)}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  students.map((enrollment) => (
                    <tr key={enrollment.id} className={isAssignable(enrollment) ? undefined : 'assign-row-inactive'}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('teacherAdmin.assign.selectStudent', { name: getStudentLabel(enrollment) })}
                          checked={selectedStudents.has(getStudentId(enrollment))}
                          onChange={() => handleSelectStudent(enrollment)}
                          disabled={assigning || !isAssignable(enrollment)}
                        />
                      </td>
                      <td>
                        {enrollment.user?.name || '-'}
                        {!isAssignable(enrollment) && (
                          <>
                            {' '}
                            <span title={t('teacherAdmin.assign.inactiveHint')}>
                              <StatusBadge tone="danger">{t('teacherAdmin.assign.inactiveBadge')}</StatusBadge>
                            </span>
                          </>
                        )}
                      </td>
                      <td>{enrollment.user?.email || enrollment.user?.phone || '-'}</td>
                      <td>
                        {(enrollment.progress ?? enrollment.progressPercent) != null
                          ? `${Math.round(enrollment.progress ?? enrollment.progressPercent)}%`
                          : '-'}
                      </td>
                      <td>
                        {enrollment.enrolledAt
                          ? formatDate(enrollment.enrolledAt)
                          : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {students.length > 0 && (
            <div className="assign-students-footer">
              <span>
                {t('teacherAdmin.assign.showingCount', {
                  shown: students.length,
                  total: studentsMeta?.total ?? students.length,
                })}
              </span>
              {hasMoreStudents && (
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary ds-btn-sm"
                  onClick={loadMoreStudents}
                  disabled={studentsLoading || assigning}
                >
                  {studentsLoading ? t('teacherAdmin.assign.loadingMore') : t('teacherAdmin.assign.loadMore')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AssignStudents;
